import React, { useEffect, useMemo, useState } from "react";
import { Navigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Check, X as XIcon, Clock, ShieldCheck, Loader2, Search, MapPin, Phone,
  BadgeCheck, RefreshCw, ClipboardCheck, FileText, Pencil,
} from "lucide-react";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import PayrollScreenFormDrawer from "../components/PayrollScreenFormDrawer";

/**
 * Approval queue for Payroll Manpower Medical / Induction submissions ("PAY-ROSTER").
 * Mirrors WorkmenApprovalsPage but operates on db.contractors where is_payroll=true
 * and pay_roster_status='pending'.
 */
export default function PayRosterApprovalsPage() {
  const { user, loading: authLoading, can } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [busyId, setBusyId] = useState("");
  const [rejectId, setRejectId] = useState("");
  const [rejectReason, setRejectReason] = useState("");
  // Admins can correct a submission (typos, mis-mapped NCC site, blood-group
  // mix-up, missing Form-XI fields) before approving. The Medical / Induction
  // entry drawer is reused so the form is 1:1 with the contractor's own
  // submission — no separate "admin-edit" surface to maintain.
  const [editingContractor, setEditingContractor] = useState(null);

  async function load() {
    setLoading(true);
    try {
      const r = await api.get("/pay-roster/pending");
      setRows(r.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not load pending Pay Roster submissions");
    } finally {
      setLoading(false);
    }
  }
  useEffect(() => { if (user) load(); }, [user]);

  const visible = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return rows;
    return rows.filter((c) =>
      [c.name, c.contact_phone, c.ncc_site_name, (c.trades || []).join(" "), c.id_proof_number]
        .filter(Boolean).join(" ").toLowerCase().includes(needle),
    );
  }, [rows, q]);

  async function approve(c) {
    setBusyId(c.id);
    try {
      await api.post(`/pay-roster/${c.id}/approve`);
      toast.success(`Pay Roster approved · ${c.name}`);
      setRows((arr) => arr.filter((x) => x.id !== c.id));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Approval failed");
    } finally { setBusyId(""); }
  }
  async function approveMedical(c) {
    setBusyId(c.id);
    try {
      const r = await api.post(`/pay-roster/${c.id}/approve-medical`);
      toast.success(`Medical approved · ${c.name}`);
      setRows((arr) => arr.map((x) => (x.id === c.id ? { ...x, ...r.data, photo_base64: x.photo_base64 } : x)));
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Medical approval failed");
    } finally { setBusyId(""); }
  }
  async function submitReject() {
    if (!rejectId) return;
    setBusyId(rejectId);
    try {
      await api.post(`/pay-roster/${rejectId}/reject`, { reason: rejectReason });
      toast.success("Rejected");
      setRows((arr) => arr.filter((x) => x.id !== rejectId));
      setRejectId(""); setRejectReason("");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Reject failed");
    } finally { setBusyId(""); }
  }

  if (authLoading) return null;
  if (!user) return <Navigate to="/admin/login" replace />;
  if (!can("approve_payroll_roster") && !can("approve_medical")) {
    return (
      <div className="mx-auto max-w-xl px-4 py-20 text-center" data-testid="pay-roster-approvals-denied">
        <div className="border-2 border-slate-900 bg-white p-8 shadow-brutal">
          <ShieldCheck className="mx-auto h-10 w-10 text-slate-700" />
          <h1 className="mt-4 font-heading text-2xl font-black uppercase">Access denied</h1>
          <p className="mt-2 text-slate-600">You need the <b>Approve Payroll Roster (Medical / Induction)</b> permission to see this page.</p>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-7xl px-4 py-8 md:px-8" data-testid="pay-roster-approvals-page">
      <div className="flex items-end justify-between gap-3">
        <div>
          <div className="industrial-label text-ncc-gold2">// Admin · Payroll Manpower · Medical / Induction</div>
          <h1 className="mt-1 flex flex-wrap items-center gap-3 font-heading text-3xl font-black uppercase tracking-tight text-slate-900 md:text-5xl">
            <span className="relative inline-flex items-center gap-2 rounded-md border-2 border-amber-300 bg-amber-50 px-3 py-2 text-amber-800" data-testid="pay-roster-heading-badge">
              <ClipboardCheck className="h-6 w-6 md:h-8 md:w-8" />
              {rows.length > 0 && (
                <span
                  data-testid="pay-roster-heading-count"
                  className="inline-flex h-6 min-w-[24px] items-center justify-center rounded-full border-2 border-red-700 bg-red-600 px-1.5 font-mono text-xs font-bold text-white md:h-7 md:min-w-[28px] md:text-sm"
                >
                  {rows.length}
                </span>
              )}
            </span>
            <span>Approval-Pay-Roster</span>
          </h1>
          <p className="mt-1 text-sm text-slate-600">
            Payroll Manpower records that submitted the Medical / Induction form are <b>pending</b> until you approve them.
            Approval marks the record fit for Form-XI, Screening Form &amp; Identity Card issuance.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          data-testid="pay-roster-approvals-refresh"
          className="inline-flex items-center gap-2 border-2 border-slate-900 bg-white px-3 py-2 industrial-label hover:-translate-y-0.5 hover:shadow-brutal-sm"
        >
          <RefreshCw className="h-4 w-4" /> Refresh
        </button>
      </div>

      <div className="mt-6 flex items-center gap-3">
        <div className="relative flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search name, mobile, site, trade, ID…"
            data-testid="pay-roster-approvals-search"
            className="w-full border-2 border-slate-300 bg-white py-2 pl-9 pr-3 font-mono text-sm focus:border-slate-900 focus:outline-none"
          />
        </div>
        <div className="industrial-label text-slate-600" data-testid="pay-roster-approvals-count">
          {loading ? "Loading…" : `${visible.length} pending`}
        </div>
      </div>

      <div className="mt-5">
        {loading ? (
          <div className="grid place-items-center py-16 text-slate-500"><Loader2 className="h-6 w-6 animate-spin" /></div>
        ) : visible.length === 0 ? (
          <div className="grid place-items-center gap-2 border-2 border-dashed border-slate-300 bg-slate-50 py-16 text-center" data-testid="pay-roster-approvals-empty">
            <BadgeCheck className="h-10 w-10 text-emerald-500" />
            <div className="font-heading text-lg font-bold text-slate-700">All caught up</div>
            <div className="font-mono text-xs text-slate-500">No Payroll Manpower medical/induction forms are awaiting approval.</div>
          </div>
        ) : (
          <ul className="space-y-3" data-testid="pay-roster-approvals-list">
            {visible.map((c) => (
              <li
                key={c.id}
                data-testid={`pay-roster-approval-row-${c.id}`}
                className="flex flex-col gap-3 border-2 border-amber-300 bg-white p-4 shadow-brutal-sm sm:flex-row sm:items-center"
              >
                <div className="grid h-16 w-16 shrink-0 place-items-center overflow-hidden rounded-full border-2 border-amber-400 bg-slate-100">
                  {c.photo_base64 ? (
                    <img src={c.photo_base64} alt={c.name} className="h-full w-full object-cover" />
                  ) : (
                    <Clock className="h-6 w-6 text-amber-500" />
                  )}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-2">
                    <h3 className="font-heading text-lg font-extrabold text-slate-900">{c.name}</h3>
                    <span className="inline-flex items-center gap-1 rounded bg-amber-100 px-1.5 py-0.5 text-[10px] font-bold text-amber-800">
                      <Clock className="h-3 w-3" /> PENDING
                    </span>
                    {(c.trades || []).slice(0, 2).map((t) => (
                      <span key={t} className="border border-slate-300 bg-slate-50 px-1.5 py-0.5 industrial-label text-[10px]">{t}</span>
                    ))}
                  </div>
                  <div className="mt-1 grid grid-cols-1 gap-1 font-mono text-xs text-slate-700 sm:grid-cols-3">
                    <div className="flex items-center gap-1.5"><Phone className="h-3 w-3 text-ncc-navy" /> {c.contact_phone || "—"}</div>
                    <div className="flex items-center gap-1.5"><MapPin className="h-3 w-3 text-ncc-navy" /> {c.ncc_site_name || <span className="text-amber-600">No site assigned</span>}</div>
                    <div className="truncate"><span className="industrial-label text-slate-500">Source:</span> {(c.source || "—")}{c.source_agency_name ? ` (${c.source_agency_name})` : ""}</div>
                  </div>
                  <div className="mt-1 font-mono text-[11px] text-slate-500 flex flex-wrap gap-x-3">
                    {c.age_years ? <span>{c.age_years} yrs</span> : null}
                    {c.blood_group ? <span>{c.blood_group}</span> : null}
                    {c.id_proof_type && c.id_proof_number ? <span>{c.id_proof_type} {c.id_proof_number.slice(0, 4)}••{c.id_proof_number.slice(-2)}</span> : null}
                    {c.pay_roster_submitted_at ? <span><FileText className="inline h-3 w-3" /> Submitted {new Date(c.pay_roster_submitted_at).toLocaleDateString()}</span> : null}
                  </div>
                </div>
                <div className="flex shrink-0 items-center gap-2">
                  {(() => {
                    const medicalOk = c.pay_roster_medical_status === "approved";
                    return (
                      <>
                        {medicalOk ? (
                          <span className="inline-flex items-center gap-1 border-2 border-emerald-500 bg-emerald-50 px-2 py-1 industrial-label text-[10px] text-emerald-700" data-testid={`pay-roster-medical-ok-${c.id}`}>
                            <Check className="h-3 w-3" /> Medical ✓
                          </span>
                        ) : can("approve_medical") ? (
                          <button
                            type="button"
                            onClick={() => approveMedical(c)}
                            disabled={busyId === c.id}
                            data-testid={`pay-roster-approve-medical-${c.id}`}
                            title="Stage 1 — approve Medical (Medical Officer)"
                            className="inline-flex items-center gap-1.5 border-2 border-rose-600 bg-rose-600 px-3 py-2 industrial-label text-[11px] text-white hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" /> Approve Medical
                          </button>
                        ) : (
                          <span className="inline-flex items-center gap-1 border-2 border-amber-400 bg-amber-50 px-2 py-1 industrial-label text-[10px] text-amber-700" data-testid={`pay-roster-medical-pending-${c.id}`}>
                            <Clock className="h-3 w-3" /> Medical Pending
                          </span>
                        )}
                        {(can("edit_contractors") || can("approve_medical") || can("approve_payroll_roster")) && (
                          <button
                            type="button"
                            onClick={() => setEditingContractor(c)}
                            data-testid={`pay-roster-edit-${c.id}`}
                            title="Correct this submission before approval"
                            className="inline-flex items-center gap-1.5 border-2 border-ncc-navy bg-white px-3 py-2 industrial-label text-[11px] text-ncc-navy hover:bg-ncc-tint"
                          >
                            <Pencil className="h-3.5 w-3.5" /> Edit
                          </button>
                        )}
                        {can("approve_payroll_roster") && (
                          <button
                            type="button"
                            onClick={() => approve(c)}
                            disabled={busyId === c.id || !medicalOk}
                            data-testid={`pay-roster-approve-${c.id}`}
                            title={medicalOk ? "Stage 2 — approve Induction (HSE)" : "Medical approval pending — the Medical Officer must approve first"}
                            className="inline-flex items-center gap-1.5 border-2 border-emerald-600 bg-emerald-600 px-3 py-2 industrial-label text-[11px] text-white hover:-translate-y-0.5 hover:shadow-brutal-sm disabled:cursor-not-allowed disabled:opacity-50"
                          >
                            <Check className="h-3.5 w-3.5" /> Approve Induction
                          </button>
                        )}
                        <button
                          type="button"
                          onClick={() => { setRejectId(c.id); setRejectReason(""); }}
                          data-testid={`pay-roster-reject-${c.id}`}
                          className="inline-flex items-center gap-1.5 border-2 border-red-500 bg-white px-3 py-2 industrial-label text-[11px] text-red-700 hover:bg-red-50"
                        >
                          <XIcon className="h-3.5 w-3.5" /> Reject
                        </button>
                      </>
                    );
                  })()}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Reject reason modal */}
      {rejectId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4" onClick={() => setRejectId("")}>
          <div onClick={(e) => e.stopPropagation()} className="w-full max-w-md border-2 border-slate-900 bg-white shadow-brutal-lg" data-testid="pay-roster-reject-modal">
            <div className="border-b-2 border-slate-900 bg-red-600 px-4 py-3 text-white">
              <div className="industrial-label">Reject Pay Roster Submission</div>
            </div>
            <div className="p-4 space-y-2">
              <div className="font-mono text-xs text-slate-600">Reason (optional, stored in the audit log):</div>
              <textarea
                value={rejectReason}
                onChange={(e) => setRejectReason(e.target.value)}
                rows={3}
                data-testid="pay-roster-reject-reason"
                className="w-full border-2 border-slate-300 bg-slate-50 px-3 py-2 font-mono text-sm focus:border-slate-900 focus:outline-none"
                placeholder="e.g. Missing Form-XI fields, photograph unclear"
              />
            </div>
            <div className="flex items-center justify-end gap-2 border-t-2 border-slate-900 bg-slate-50 px-4 py-3">
              <button type="button" onClick={() => setRejectId("")} className="border-2 border-slate-900 bg-white px-3 py-1.5 industrial-label text-[11px]">Cancel</button>
              <button type="button" onClick={submitReject} disabled={busyId === rejectId} data-testid="pay-roster-reject-confirm" className="border-2 border-red-600 bg-red-600 px-3 py-1.5 industrial-label text-[11px] text-white disabled:opacity-50">Confirm Reject</button>
            </div>
          </div>
        </div>
      )}

      {/* Admin-edit drawer — reuses the Medical / Induction form. On save
          the row is refreshed in place so corrections are visible
          immediately and the admin can then proceed to Approve. */}
      <PayrollScreenFormDrawer
        open={!!editingContractor}
        contractor={editingContractor}
        authToken={typeof window !== "undefined" ? window.localStorage.getItem("ccdb_token") : ""}
        adminMode
        onClose={() => setEditingContractor(null)}
        onSaved={(updated) => {
          setEditingContractor(null);
          if (updated && updated.id) {
            // Merge updated fields back into the list so the card reflects
            // the correction instantly. Records that get approved as part
            // of the save flow are removed elsewhere by `load()`.
            setRows((arr) => arr.map((x) => (x.id === updated.id ? { ...x, ...updated } : x)));
          } else {
            load();
          }
          toast.success("Submission updated");
        }}
      />
    </div>
  );
}
